import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';
import dayjs from 'dayjs';
import {FiArrowRight, FiGlobe} from 'react-icons/fi';
import {Button} from '@ui/atoms/button';
import {CustomMarkdown} from '@ui/atoms/typography/markdown';
import {UserListCard} from '@ui/molecules/user/list-card';
import {Avatars} from '@ui/molecules/avatars';
import {ItemLoader} from '@ui/organisms/item-loader';
import {SecondaryEyeOffIcon} from '@ui/atoms/icons/eye-off';
import {HoverTooltip} from '@ui/molecules/hover-tooltip';
import {useStoreContext} from '@store/store-context';
import {capitalize} from '@utils';
import {userName} from '@utils/user-name';
import {Feedback, FeedbackRequest} from './feedback-items';
import {
  FeedbackChip,
  FeedbackDetailCard,
  FeedbackEmpty,
  FeedbackReceivedHeader,
  FeedbackRequestsHeader,
  FeedbackStack,
} from './feedback-page.styles';
import {LoadingWrapper} from './feedback-styles';

type FeedVariant = 'for-you' | 'sent' | 'praise';

interface FeedbackFeedProps {
  data?: Feedback[];
  requests?: FeedbackRequest[];
  variant: FeedVariant;
  isLoading?: boolean;
  sortOrder?: 'asc' | 'desc';
  emptyState?: {title: string; description: string};
  pageKey?: string;
  onDismissRequest?: (request: FeedbackRequest) => void;
  onDeleteRequest?: (request: FeedbackRequest) => void;
  onDeletePraise?: (praise: Feedback) => void;
  onRespond?: (request: FeedbackRequest) => void;
}

const idOf = (value: string | {id?: string} | undefined) =>
  typeof value === 'string' ? value : value?.id || '';

export const normaliseFeedbackResponse = (response: any): Feedback[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

export const normaliseRequestResponse = (response: any): FeedbackRequest[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const FeedbackFeed = observer(
  ({
    data = [],
    requests = [],
    variant,
    isLoading,
    sortOrder = 'desc',
    emptyState,
    pageKey,
    onDismissRequest,
    onDeleteRequest,
    onDeletePraise,
    onRespond,
  }: FeedbackFeedProps) => {
    const [visibleCount, setVisibleCount] = useState(20);

    useEffect(() => setVisibleCount(20), [pageKey]);
    const {
      authStore: {auth},
      usersStore: {users},
    } = useStoreContext();

    const findUser = (value: string | {id?: string} | undefined) =>
      users.find((user) => user.id === idOf(value));

    const avatarFor = (value: string | {id?: string} | undefined) => {
      const user = findUser(value);
      return {
        id: user?.id || idOf(value),
        name: user ? userName(user) : 'Workspace member',
        src: user?.avatar?.url || '',
      };
    };

    const sortDirection = sortOrder === 'asc' ? 1 : -1;
    const sortedFeedback = [...data].sort(
      (a, b) =>
        sortDirection *
        (Number(new Date(a.createdAt || a.updatedAt)) -
          Number(new Date(b.createdAt || b.updatedAt))),
    );
    const sortedRequests = [...requests].sort(
      (a, b) =>
        sortDirection *
        (Number(new Date(a.createdAt)) - Number(new Date(b.createdAt))),
    );
    const pendingRequestCount = sortedRequests.filter(
      ({status}) => status !== 'completed',
    ).length;

    if (isLoading) {
      return (
        <LoadingWrapper>
          <ItemLoader />
        </LoadingWrapper>
      );
    }

    const renderRequest = (request: FeedbackRequest) => {
      const incoming = variant === 'for-you';
      const personId = incoming ? request.user : request.reviewer;
      const person = avatarFor(personId);

      return (
        <FeedbackDetailCard key={request.id}>
          <div className="feedback-detail-header">
            <div className="feedback-detail-heading feedback-detail-heading--request">
              <span
                className={`feedback-detail-kind ${
                  variant === 'sent'
                    ? 'feedback-detail-kind--sent-request'
                    : 'feedback-detail-kind--request'
                }`}>
                {variant === 'sent' ? 'Requests' : 'Request'}
              </span>
              <h3>{request.subjectName}</h3>
            </div>
            <div className="feedback-request-actions">
              <FeedbackChip
                $tone={request.status === 'completed' ? 'success' : 'neutral'}>
                {request.status === 'completed'
                  ? 'Completed'
                  : 'Awaiting response'}
              </FeedbackChip>
              {incoming && request.status !== 'completed' && onRespond && (
                <Button
                  type="button"
                  kind="secondary"
                  style={{padding: '8px 14px'}}
                  onClick={() => onRespond(request)}>
                  Respond
                </Button>
              )}
            </div>
          </div>
          <div className="feedback-request-meta">
            <div className="feedback-detail-byline">
              <strong>{incoming ? 'From' : 'To'}</strong>
              <UserListCard
                tooltip
                userId={person.id}
                type="aligned"
                textKind="textDark"
                textStyle={{fontSize: '14px', fontWeight: 500}}
                avatar={person.src}
                name={person.name}
              />
            </div>
            <div className="feedback-request-meta-actions">
              <time
                className="feedback-detail-date"
                dateTime={request.createdAt}>
                {dayjs(request.createdAt).format('DD MMM, YYYY')}
              </time>
              {incoming && request.status !== 'completed' && onDismissRequest && (
                <button
                  className="feedback-request-dismiss"
                  type="button"
                  onClick={() => onDismissRequest(request)}>
                  Dismiss
                </button>
              )}
              {!incoming && onDeleteRequest && (
                <button
                  className="feedback-request-dismiss"
                  type="button"
                  onClick={() => onDeleteRequest(request)}>
                  Delete
                </button>
              )}
            </div>
          </div>
        </FeedbackDetailCard>
      );
    };

    const renderFeedback = (item: Feedback) => {
      const praise =
        variant === 'praise' || item.isPraise || item.group?.length > 0;
      const sender = avatarFor(item.reviewer);
      const recipientIds = praise ? item.group || [] : [idOf(item.user)];
      const recipients = recipientIds.map(avatarFor);

      if (!praise) {
        const personId = variant === 'sent' ? item.user : item.reviewer;
        const person = avatarFor(personId);
        const visibilityLabel =
          item.visibility === 'manager'
            ? 'Private + Manager'
            : capitalize(item.visibility);
        const visibilityTooltip =
          item.visibility === 'private'
            ? 'Only you can see this feedback.'
            : item.visibility === 'manager'
            ? 'Only you and your manager can see this feedback.'
            : '';
        const visibilityPill = (
          <span
            className="feedback-detail-visibility"
            tabIndex={visibilityTooltip ? 0 : undefined}
            aria-label={
              visibilityTooltip
                ? `${visibilityLabel}. ${visibilityTooltip}`
                : visibilityLabel
            }>
            {item.visibility !== 'public' && <SecondaryEyeOffIcon />}
            {visibilityLabel}
          </span>
        );

        return (
          <FeedbackDetailCard key={item.id}>
            <div className="feedback-detail-header">
              <div
                className={`feedback-detail-heading feedback-detail-heading--feedback${
                  variant === 'for-you'
                    ? ' feedback-detail-heading--received'
                    : ''
                }`}>
                <span className="feedback-detail-kind">Feedback</span>
                <h3>{item.subjectName}</h3>
              </div>
              {visibilityTooltip ? (
                <HoverTooltip
                  tooltipText={visibilityTooltip}
                  showHoverBackground={false}
                  className="bg-gray-700 text-alwaysWhite py-1 px-2 z-20"
                  tooltipStyle={{right: 0, top: 'calc(100% + 8px)'}}>
                  {visibilityPill}
                </HoverTooltip>
              ) : (
                visibilityPill
              )}
            </div>
            <div className="feedback-detail-content">
              <div className="feedback-detail-meta">
                <div className="feedback-detail-byline">
                  <strong>{variant === 'sent' ? 'To' : 'From'}</strong>
                  <UserListCard
                    tooltip
                    userId={person.id}
                    type="aligned"
                    textKind="textDark"
                    textStyle={{fontSize: '14px', fontWeight: 500}}
                    avatar={person.src}
                    name={person.name}
                  />
                </div>
                <time
                  className="feedback-detail-date"
                  dateTime={item.createdAt}>
                  {dayjs(item.createdAt).format('DD MMM, YYYY')}
                </time>
              </div>
              <div className="feedback-detail-copy">
                <CustomMarkdown
                  className="feedback-detail-copy-text"
                  text={item.feedback}
                />
              </div>
            </div>
          </FeedbackDetailCard>
        );
      }

      const senderLabel =
        idOf(item.reviewer) === auth.user.id ? 'You' : sender.name;
      const recipientNames = recipients.map(({name}) => name).join(', ');
      const subjectLabel =
        item.subjectType === 'custom'
          ? item.subjectName
          : `${capitalize(item.subjectType)}: ${item.subjectName}`;
      const canDeletePraise =
        variant === 'praise' &&
        idOf(item.reviewer) === auth.user.id &&
        Boolean(onDeletePraise);

      return (
        <FeedbackDetailCard key={item.id}>
          <div className="feedback-detail-header">
            <div className="feedback-detail-heading feedback-detail-heading--praise">
              <span className="feedback-detail-kind feedback-detail-kind--praise">
                Praise
              </span>
              <h3>{subjectLabel}</h3>
            </div>
            <span className="feedback-detail-visibility feedback-detail-visibility--public">
              <FiGlobe aria-hidden="true" />
              Public
            </span>
          </div>
          <div className="feedback-detail-content">
            <div className="feedback-praise-meta">
              <div
                className="feedback-praise-people"
                aria-label={`${senderLabel} praised ${recipientNames}`}>
                <div className="feedback-praise-person">
                  <Avatars items={[sender]} total={1} showCount={1} size="md" />
                  <span>{senderLabel}</span>
                </div>
                <FiArrowRight
                  className="feedback-praise-arrow"
                  aria-hidden="true"
                />
                <div className="feedback-praise-person feedback-praise-person--recipients">
                  <span
                    className="feedback-praise-recipients"
                    aria-label={`Praise recipients: ${recipientNames}`}>
                    <Avatars
                      items={recipients}
                      total={recipients.length}
                      showCount={3}
                      size="md"
                      tooltip={false}
                    />
                  </span>
                </div>
              </div>
              <div className="feedback-request-meta-actions">
                <time
                  className="feedback-detail-date"
                  dateTime={item.createdAt}>
                  {dayjs(item.createdAt).format('DD MMM, YYYY')}
                </time>
                {canDeletePraise && (
                  <button
                    aria-label={`Delete praise: ${subjectLabel}`}
                    className="feedback-request-dismiss"
                    type="button"
                    onClick={() => onDeletePraise?.(item)}>
                    Delete
                  </button>
                )}
              </div>
            </div>
            <div className="feedback-detail-copy">
              <CustomMarkdown
                className="feedback-detail-copy-text"
                text={item.feedback}
              />
              {recipients.length > 0 && (
                <p className="feedback-praise-recipient-names">
                  For {recipientNames}
                </p>
              )}
            </div>
          </div>
        </FeedbackDetailCard>
      );
    };

    const hasContent = sortedRequests.length > 0 || sortedFeedback.length > 0;
    if (!hasContent) {
      const copy = emptyState
        ? [emptyState.title, emptyState.description]
        : variant === 'praise'
        ? [
            'No praise yet',
            'Public recognition across your team will appear here.',
          ]
        : variant === 'sent'
        ? [
            'Nothing sent yet',
            'Feedback, praise and requests you send will appear here.',
          ]
        : [
            'Nothing here yet',
            'Feedback and requests directed to you will appear here.',
          ];
      return (
        <FeedbackEmpty>
          <h2>{copy[0]}</h2>
          <p>{copy[1]}</p>
        </FeedbackEmpty>
      );
    }

    return (
      <FeedbackStack>
        {sortedRequests.length > 0 && (
          <section>
            {variant === 'for-you' ? (
              <FeedbackRequestsHeader>
                <h2>Requests</h2>
                <span className="feedback-requests-header-dot" aria-hidden />
                <span className="feedback-requests-header-status">
                  Awaiting response
                </span>
                <span
                  className="feedback-requests-header-count"
                  aria-label={`${pendingRequestCount} awaiting response`}>
                  {pendingRequestCount}
                </span>
              </FeedbackRequestsHeader>
            ) : (
              <FeedbackRequestsHeader $tone="neutral">
                <h2>Requests</h2>
                <span className="feedback-requests-header-dot" aria-hidden />
                <span className="feedback-requests-header-status">
                  Awaiting response
                </span>
                <span
                  className="feedback-requests-header-count"
                  aria-label={`${pendingRequestCount} awaiting response`}>
                  {pendingRequestCount}
                </span>
              </FeedbackRequestsHeader>
            )}
            <FeedbackStack>{sortedRequests.map(renderRequest)}</FeedbackStack>
          </section>
        )}
        {sortedFeedback.length > 0 && (
          <section>
            {variant === 'for-you' ? (
              <FeedbackReceivedHeader>
                <h2>Feedback received ({sortedFeedback.length})</h2>
                <span aria-hidden />
              </FeedbackReceivedHeader>
            ) : variant !== 'praise' ? (
              <FeedbackReceivedHeader>
                <h2>Sent feedback ({sortedFeedback.length})</h2>
                <span aria-hidden />
              </FeedbackReceivedHeader>
            ) : null}
            <FeedbackStack>
              {sortedFeedback.slice(0, visibleCount).map(renderFeedback)}
            </FeedbackStack>
            {sortedFeedback.length > visibleCount && (
              <Button
                type="button"
                kind="secondary"
                style={{display: 'block', margin: '24px auto 0'}}
                onClick={() => setVisibleCount((count) => count + 20)}>
                Show more {variant === 'praise' ? 'praise' : 'feedback'}
              </Button>
            )}
          </section>
        )}
      </FeedbackStack>
    );
  },
);

export default FeedbackFeed;
